import { Rng } from '../core/rng';
import { BOSS_ROSTER } from '../data/bosses';
import { FAMILIES } from '../data/families';
import type { Difficulty } from '../data/types';

export const DECK_SIZE = 5;
export const BOSS_PICKS = 3;

const KEY = 'peasents.profile.v1';

/** Gold and copies to raise a unit from this level to the next. Index 0 is level 1 → 2. */
const UPGRADE_COST: Array<{ gold: number; copies: number }> = [
  { gold: 25, copies: 2 },
  { gold: 50, copies: 4 },
  { gold: 100, copies: 10 },
  { gold: 200, copies: 20 },
  { gold: 400, copies: 35 },
  { gold: 800, copies: 50 },
  { gold: 1400, copies: 70 },
  { gold: 2200, copies: 100 },
  { gold: 3500, copies: 140 },
];

export const MAX_CARD_LEVEL = UPGRADE_COST.length + 1;

export interface Profile {
  gold: number;
  levels: Record<string, number>;
  copies: Record<string, number>;
  /** Five unit families this commander brings into every match. */
  deck: string[];
  /** Three bosses this commander brings into every match. */
  bosses: string[];
}

export interface CardReward {
  gold: number;
  familyId: string;
  familyName: string;
  copies: number;
}

export interface SideLoadout {
  levels: Record<string, number>;
  /** Additive damage/health bonus from every upgrade on the account. 0.06 = +6%. */
  crit: number;
}

function takeIds(raw: unknown, allowed: readonly string[], max: number): string[] {
  const ok = new Set(allowed);
  const out: string[] = [];
  if (!Array.isArray(raw)) return out;
  for (const id of raw) {
    if (typeof id === 'string' && ok.has(id) && !out.includes(id)) out.push(id);
    if (out.length === max) break;
  }
  return out;
}

function padIds(ids: string[], pool: readonly string[], max: number): string[] {
  const out = ids.slice(0, max);
  for (const id of pool) {
    if (out.length === max) break;
    if (!out.includes(id)) out.push(id);
  }
  return out;
}

const FAMILY_IDS = FAMILIES.map((f) => f.id);
const BOSS_IDS = BOSS_ROSTER.map((b) => b.id);

/** A legal 5-unit company. Missing or unknown ids are filled from the roster. */
export function matchDeck(raw: unknown): string[] {
  return padIds(takeIds(raw, FAMILY_IDS, DECK_SIZE), FAMILY_IDS, DECK_SIZE);
}

/** A legal set of 3 bosses. */
export function matchBosses(raw: unknown): string[] {
  return padIds(takeIds(raw, BOSS_IDS, BOSS_PICKS), BOSS_IDS, BOSS_PICKS);
}

export function companyOf(p: Profile): { deck: string[]; bosses: string[] } {
  return { deck: matchDeck(p.deck), bosses: matchBosses(p.bosses) };
}

/** The training opponent brings a seeded company, not the player's. */
export function botCompany(seed: number): { deck: string[]; bosses: string[] } {
  const rng = new Rng(seed ^ 0x5bd1e995);
  return {
    deck: rng.shuffle([...FAMILY_IDS]).slice(0, DECK_SIZE),
    bosses: rng.shuffle([...BOSS_IDS]).slice(0, BOSS_PICKS),
  };
}

function blank(): Profile {
  const levels: Record<string, number> = {};
  const copies: Record<string, number> = {};
  for (const f of FAMILIES) {
    levels[f.id] = 1;
    copies[f.id] = 8;
  }
  return { gold: 200, levels, copies, deck: matchDeck([]), bosses: matchBosses([]) };
}

export function loadProfile(): Profile {
  const base = blank();
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return base;
    const parsed = JSON.parse(raw) as Partial<Profile>;
    return {
      gold: typeof parsed.gold === 'number' ? parsed.gold : base.gold,
      levels: { ...base.levels, ...(parsed.levels ?? {}) },
      copies: { ...base.copies, ...(parsed.copies ?? {}) },
      deck: matchDeck(parsed.deck),
      bosses: matchBosses(parsed.bosses),
    };
  } catch {
    return base;
  }
}

export function saveProfile(p: Profile): void {
  localStorage.setItem(KEY, JSON.stringify(p));
}

export function cardLevel(p: Profile, familyId: string): number {
  return p.levels[familyId] ?? 1;
}

export function cardCopies(p: Profile, familyId: string): number {
  return p.copies[familyId] ?? 0;
}

/** This unit's own stat multiplier from its card level. */
export function unitPower(level: number): number {
  return 1 + 0.12 * (Math.max(1, level) - 1);
}

export function critBonus(levels: Record<string, number>): number {
  let extra = 0;
  for (const level of Object.values(levels)) extra += Math.max(0, level - 1);
  return extra * 0.02;
}

/** Shown like Rush Royale's crit: 100% at the start, +2% per upgrade. */
export function critPercent(p: Profile): number {
  return Math.round((1 + critBonus(p.levels)) * 100);
}

export function loadoutFromProfile(p: Profile): SideLoadout {
  return { levels: { ...p.levels }, crit: critBonus(p.levels) };
}

export function upgradeCost(level: number): { gold: number; copies: number } | null {
  if (level < 1 || level >= MAX_CARD_LEVEL) return null;
  return UPGRADE_COST[level - 1] ?? null;
}

export function upgradeBlock(p: Profile, familyId: string): string | null {
  const level = cardLevel(p, familyId);
  const cost = upgradeCost(level);
  if (!cost) return 'Max level';
  if (p.gold < cost.gold) return `Need ${cost.gold - p.gold} more gold`;
  const have = cardCopies(p, familyId);
  if (have < cost.copies) return `Need ${cost.copies - have} more cards`;
  return null;
}

export function upgradeCard(p: Profile, familyId: string): boolean {
  if (upgradeBlock(p, familyId)) return false;
  const level = cardLevel(p, familyId);
  const cost = upgradeCost(level)!;
  p.gold -= cost.gold;
  p.copies[familyId] = cardCopies(p, familyId) - cost.copies;
  p.levels[familyId] = level + 1;
  saveProfile(p);
  return true;
}

export function grantMatchReward(p: Profile, result: 'win' | 'loss' | 'draw'): CardReward {
  const gold = result === 'win' ? 45 : result === 'draw' ? 25 : 18;
  const copies = result === 'win' ? 2 : 1;
  const family = FAMILIES[Math.floor(Math.random() * FAMILIES.length)];
  p.gold += gold;
  p.copies[family.id] = cardCopies(p, family.id) + copies;
  saveProfile(p);
  return { gold, copies, familyId: family.id, familyName: family.minion.name };
}

/** Training opponents scale with the difficulty you picked, not with your account. */
export function botLoadout(difficulty: Difficulty): SideLoadout {
  const level = difficulty === 'warlord' ? 3 : difficulty === 'veteran' ? 2 : 1;
  const levels: Record<string, number> = {};
  for (const f of FAMILIES) levels[f.id] = level;
  return { levels, crit: critBonus(levels) };
}
